import { Controller } from "@hotwired/stimulus"

/**
 * Survey Filter Controller
 * Manages tabs switching, search with debounce, and tag chip removal
 */
export default class extends Controller {
  static targets = ["form", "searchInput", "tab", "clearButton"]
  static values = {
    debounceDelay: { type: Number, default: 300 }
  }

  connect() {
    this.debounceTimer = null
    this.restoreSearchFocus()
    this.updateClearButton()
  }

  disconnect() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
  }

  /**
   * Switch tab and submit form
   */
  switchTab(event) {
    event.preventDefault()

    const clickedTab = event.currentTarget
    const filterType = clickedTab.dataset.filterType

    // Update active state visually
    this.tabTargets.forEach(tab => {
      tab.classList.remove("survey-tab--active")
    })
    clickedTab.classList.add("survey-tab--active")

    // Update hidden field and submit
    const filterInput = this.formTarget.querySelector('input[name="search[filter_type]"]')
    if (filterInput) {
      filterInput.value = filterType
    }

    this.submitForm()
  }

  /**
   * Search with debounce
   */
  search() {
    if (this.hasSearchInputTarget) {
      sessionStorage.setItem("filterFocusInput", "true")
      sessionStorage.setItem("filterCursorPos", String(this.searchInputTarget.selectionEnd || 0))
    }

    this.updateClearButton()

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }

    this.debounceTimer = setTimeout(() => {
      this.submitForm()
    }, this.debounceDelayValue)
  }

  /**
   * Clear the search input and submit the form to reset results.
   */
  clearSearch(event) {
    if (event) event.preventDefault()
    if (!this.hasSearchInputTarget) return

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }

    this.searchInputTarget.value = ""
    this.updateClearButton()
    sessionStorage.removeItem("filterFocusInput")
    sessionStorage.removeItem("filterCursorPos")
    this.submitForm()
  }

  /**
   * Toggle the visibility of the clear button based on whether the input
   * has any text.
   */
  updateClearButton() {
    if (!this.hasClearButtonTarget || !this.hasSearchInputTarget) return
    this.clearButtonTarget.hidden = this.searchInputTarget.value.length === 0
  }

  /**
   * Restore focus to the search input after a frame update.
   * Stimulus controllers are re-instantiated when the turbo-frame replaces
   * the DOM, so we persist the cursor position via sessionStorage.
   */
  restoreSearchFocus() {
    if (sessionStorage.getItem("filterFocusInput") !== "true") return
    if (!this.hasSearchInputTarget) return

    const pos = parseInt(sessionStorage.getItem("filterCursorPos") || "0", 10)
    this.searchInputTarget.focus()
    try {
      this.searchInputTarget.setSelectionRange(pos, pos)
    } catch (e) {
      // setSelectionRange is not supported on every input type
    }
    sessionStorage.removeItem("filterFocusInput")
    sessionStorage.removeItem("filterCursorPos")
  }

  /**
   * Remove a tag chip and submit form
   */
  removeTag(event) {
    event.preventDefault()

    const tagId = event.currentTarget.dataset.tagId
    const select = this.formTarget.querySelector('select[name="search[tags][]"]')

    if (select) {
      // Deselect the option in the select
      const options = select.options
      for (let i = 0; i < options.length; i++) {
        if (options[i].value === tagId) {
          options[i].selected = false
          break
        }
      }

      // Trigger change event for select2 if present
      const changeEvent = new Event("change", { bubbles: true })
      select.dispatchEvent(changeEvent)
    }

    this.submitForm()
  }

  /**
   * Submit the form via Turbo
   * Uses submit() to bypass HTML5 validation
   */
  submitForm() {
    if (this.hasFormTarget) {
      // Disable validation before submitting
      this.formTarget.setAttribute('novalidate', '')
      this.formTarget.requestSubmit()
    }
  }
}
